// src/components/TieredPricingManager.tsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  TextField,
  IconButton,
  Divider,
  CircularProgress,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import {
  fetchTieredPricesByProduct,
  createTieredPrice,
  updateTieredPrice,
  deleteTieredPrice,
} from "../api/tieredPricingApi";

interface TieredPrice {
  id?: number;
  product_id: number;
  variant_id?: number | null;
  min_quantity: number;
  price_per_unit: number;
}

interface ApiError {
  response?: { data?: { error?: string } };
  message?: string;
}

interface TieredPricingManagerProps {
  productId: number;
  variantId?: number | null;
  variantName?: string;
}

const TieredPricingManager: React.FC<TieredPricingManagerProps> = ({
  productId,
  variantId = null,
  variantName,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [tieredPrices, setTieredPrices] = useState<TieredPrice[]>([]);
  const [newTier, setNewTier] = useState<TieredPrice>({
    product_id: productId,
    variant_id: variantId,
    min_quantity: 1,
    price_per_unit: 0,
  });
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<number | null>(null); // per-row saving state

  const loadTiers = async () => {
    try {
      setLoading(true);
      const prices = await fetchTieredPricesByProduct(productId);
      const filtered = variantId
        ? prices.filter((p: TieredPrice) => p.variant_id === variantId)
        : prices.filter((p: TieredPrice) => !p.variant_id);
      setTieredPrices(filtered);
    } catch (error: unknown) {
      console.error("Error loading tiered prices:", error);
      let message = "Failed to load tiered prices";
      if (typeof error === "object" && error !== null && "response" in error) {
        const apiError = error as ApiError;
        if (apiError.response?.data?.error) {
          message = apiError.response.data.error;
        }
      }
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (productId) loadTiers();
  }, [productId, variantId]);

  const handleAddTier = async () => {
    try {
      if (newTier.min_quantity <= 0 || newTier.price_per_unit <= 0) {
        enqueueSnackbar("Min Quantity and Price must be greater than 0", { variant: "warning" });
        return;
      }

      const duplicate = tieredPrices.some(
        (tier) =>
          tier.min_quantity === newTier.min_quantity &&
          (tier.variant_id ?? null) === (variantId ?? null)
      );
      if (duplicate) {
        enqueueSnackbar("This quantity tier already exists", { variant: "error" });
        return;
      }

      const created = await createTieredPrice({
        product_id: productId,
        variant_id: variantId,
        min_quantity: newTier.min_quantity,
        price_per_unit: newTier.price_per_unit,
      });
      setTieredPrices((prev) => [...prev, created]);
      setNewTier((prev) => ({ ...prev, min_quantity: 1, price_per_unit: 0 }));
      enqueueSnackbar("Tier added successfully", { variant: "success" });
    } catch (error: unknown) {
      console.error("Error creating tiered price:", error);
      let message = "Failed to add tier";
      if (typeof error === "object" && error !== null && "response" in error) {
        const apiError = error as ApiError;
        if (apiError.response?.data?.error) {
          message = apiError.response.data.error;
        }
      }
      enqueueSnackbar(message, { variant: "error" });
    }
  };

  // Persist a single tier row to the backend (called onBlur / Enter)
  const persistTier = async (id: number) => {
    const tier = tieredPrices.find((t) => t.id === id);
    if (!tier) return;

    // Basic validation
    if (!tier.min_quantity || tier.min_quantity <= 0) {
      enqueueSnackbar("Quantity must be greater than 0", { variant: "warning" });
      return;
    }
    if (!tier.price_per_unit || tier.price_per_unit <= 0) {
      enqueueSnackbar("Price must be greater than 0", { variant: "warning" });
      return;
    }

    // Prevent duplicates on the same product/variant by min_quantity
    const duplicate = tieredPrices.some(
      (t) =>
        t.id !== id &&
        t.min_quantity === tier.min_quantity &&
        (t.variant_id ?? null) === (tier.variant_id ?? null)
    );
    if (duplicate) {
      enqueueSnackbar("This quantity tier already exists", { variant: "error" });
      return;
    }

    try {
      setSavingId(id);
      await updateTieredPrice(id, {
        min_quantity: tier.min_quantity,
        price_per_unit: tier.price_per_unit,
      });
      enqueueSnackbar("Tier updated successfully", { variant: "success" });
    } catch (error: unknown) {
      console.error("Error updating tier:", error);
      let message = "Failed to update tier";
      if (typeof error === "object" && error !== null && "response" in error) {
        const apiError = error as ApiError;
        if (apiError.response?.data?.error) {
          message = apiError.response.data.error;
        }
      }
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setSavingId(null);
    }
  };

  const handleDeleteTier = async (id: number) => {
    try {
      await deleteTieredPrice(id);
      setTieredPrices((prev) => prev.filter((tier) => tier.id !== id));
      enqueueSnackbar("Tier deleted successfully", { variant: "success" });
    } catch (error: unknown) {
      console.error("Error deleting tier:", error);
      let message = "Failed to delete tier";
      if (typeof error === "object" && error !== null && "response" in error) {
        const apiError = error as ApiError;
        if (apiError.response?.data?.error) {
          message = apiError.response.data.error;
        }
      }
      enqueueSnackbar(message, { variant: "error" });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="subtitle1" fontWeight={600} mb={2}>
        {variantName ? `Tiered Pricing for: ${variantName}` : "Tiered Pricing (Optional)"}
      </Typography>

      {tieredPrices.length === 0 && (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          No tiered pricing added yet.
        </Typography>
      )}

      {tieredPrices
        .sort((a, b) => a.min_quantity - b.min_quantity)
        .map((tier) => (
          <Stack
            key={tier.id}
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <TextField
              label="Min Quantity"
              type="number"
              size="small"
              inputProps={{ step: 1, min: 1 }}
              value={tier.min_quantity}
              onChange={(e) => {
                const val = e.target.value === "" ? 0 : parseInt(e.target.value, 10);
                setTieredPrices((prev) =>
                  prev.map((t) => (t.id === tier.id ? { ...t, min_quantity: isNaN(val) ? 0 : val } : t))
                );
              }}
              onBlur={() => persistTier(tier.id!)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  (e.target as HTMLInputElement).blur();
                }
              }}
            />
            <TextField
              label="Price per Unit ($)"
              type="number"
              size="small"
              inputProps={{ step: 0.01, min: 0 }}
              value={tier.price_per_unit}
              onChange={(e) => {
                const val = e.target.value === "" ? 0 : parseFloat(e.target.value);
                setTieredPrices((prev) =>
                  prev.map((t) =>
                    t.id === tier.id ? { ...t, price_per_unit: isNaN(val) ? 0 : val } : t
                  )
                );
              }}
              onBlur={() => persistTier(tier.id!)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  (e.target as HTMLInputElement).blur();
                }
              }}
            />

            <IconButton
              color="error"
              onClick={() => handleDeleteTier(tier.id!)}
              disabled={savingId === tier.id}
            >
              <Delete />
            </IconButton>

            {savingId === tier.id && <CircularProgress size={18} />}
          </Stack>
        ))}

      <Divider sx={{ my: 3 }} />

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
        <TextField
          label="Min Quantity"
          type="number"
          size="small"
          inputProps={{ step: 1, min: 1 }}
          value={newTier.min_quantity}
          onChange={(e) =>
            setNewTier({
              ...newTier,
              min_quantity: e.target.value === "" ? 0 : parseInt(e.target.value, 10) || 0,
            })
          }
        />
        <TextField
          label="Price Per Unit ($)"
          type="number"
          size="small"
          inputProps={{ step: 0.01, min: 0 }}
          value={newTier.price_per_unit}
          onChange={(e) =>
            setNewTier({
              ...newTier,
              price_per_unit: e.target.value === "" ? 0 : parseFloat(e.target.value) || 0,
            })
          }
        />
        <Button variant="contained" startIcon={<Add />} onClick={handleAddTier} disabled={loading}>
          Add Tier
        </Button>
      </Stack>
    </Box>
  );
};

export default TieredPricingManager;
